import { useState, useRef, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";

// Interactive, user-controlled before/after comparison slider (no autoplay).
const BeforeAfterSlider = ({ before, after, beforeAlt, afterAlt }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const move = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    let pct = ((clientX - rect.left) / rect.width) * 100;
    pct = Math.max(0, Math.min(100, pct));
    setPos(pct);
  }, []);

  const onMouseDown = () => (dragging.current = true);
  const onMouseUp = () => (dragging.current = false);
  const onMouseMove = (e) => dragging.current && move(e.clientX);
  const onTouchMove = (e) => move(e.touches[0].clientX);

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[3/2] overflow-hidden select-none border-2 border-[#0F2E4C] bg-slate-200"
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onTouchMove={onTouchMove}
      data-testid="before-after-slider"
    >
      {/* After image (full) */}
      <img src={after} alt={afterAlt} loading="lazy" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      <span className="absolute top-3 right-3 bg-[#16A34A] text-white text-xs font-bold uppercase tracking-wide px-2 py-1 z-10">After</span>

      {/* Before image (clipped) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
        <img src={before} alt={beforeAlt} loading="lazy" className="absolute inset-0 h-full object-cover" style={{ width: containerRef.current ? containerRef.current.offsetWidth : "100%", maxWidth: "none" }} />
        <span className="absolute top-3 left-3 bg-[#0F2E4C] text-white text-xs font-bold uppercase tracking-wide px-2 py-1">Before</span>
      </div>

      {/* Handle */}
      <div className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)] ba-slider-handle" style={{ left: `${pos}%`, transform: "translateX(-50%)" }} onMouseDown={onMouseDown} onTouchStart={onMouseDown}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 bg-white border-2 border-[#0F2E4C] flex items-center justify-center cursor-ew-resize" data-testid="slider-handle">
          <MoveHorizontal className="w-5 h-5 text-[#0F2E4C]" />
        </div>
      </div>

      {/* Range input for accessibility / keyboard */}
      <input
        type="range"
        min="0"
        max="100"
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        aria-label="Drag to compare before and after"
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
        data-testid="slider-range"
      />
    </div>
  );
};

export default BeforeAfterSlider;
